import {
  AfterContentChecked,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  Input,
  QueryList,
} from '@angular/core';
import { GridRowDataProvider } from '@assetaccountant/core/directives/grid/grid-row-data.provider';
import { GridRowDataDirective } from '@assetaccountant/core/directives/grid/row-data.directive';
import { CsvBuilder } from '@assetaccountant/core/services/csv-builder';
import { firstWithContext } from '@assetaccountant/core/utils/first-with-context';
import { NgbDropdownItem } from '@ng-bootstrap/ng-bootstrap';
import { Observable, Unsubscribable, isObservable, of } from 'rxjs';
import { GridTableComponent } from '../grid/grid-table/grid-table.component';

export interface GridExportParams<T extends {}> {
  fileName: string;
  columnFilter?(key: string): boolean;
  rowFilter?(data: T): boolean;
  prepend?: string[];
  processCellCallback?(params: { colId: string; data: T; value: any }): string;
}

@Component({
  selector: 'app-export-button',
  templateUrl: './export-button.component.html',
  styleUrls: ['./export-button.component.scss'],
  standalone: false,
})
export class ExportButtonComponent<T extends {} = any> implements AfterContentChecked {
  @Input() gridExportParams: GridExportParams<T> | Observable<GridExportParams<T>>;
  @Input() size: 'small' | null | undefined;
  @Input() description?: string;

  @ContentChildren(NgbDropdownItem, { descendants: true })
  additionalDropdownItems: QueryList<NgbDropdownItem>;

  private _source?: GridTableComponent<T>;
  get source() {
    return this._source;
  }

  disabled = true;

  get showDropdown() {
    return !this.source || (this.additionalDropdownItems?.length ?? 0) > 0;
  }

  constructor(
    private csvBuilder: CsvBuilder,
    private cd: ChangeDetectorRef,
  ) {}

  register(source: GridTableComponent<T>): Unsubscribable {
    this._source = source;
    this.onSourceChange();

    return {
      unsubscribe: () => {
        if (this._source === source) {
          this._source = undefined;
          this.onSourceChange();
        }
      },
    };
  }

  private onSourceChange() {
    if (this.source) {
      assertRowDataProvider(this.source.rowDataProvider);
      this.disabled = false;
    } else {
      this.disabled = true;
    }

    this.cd.detectChanges();
  }

  ngAfterContentChecked(): void {
    this.cd.detectChanges();
  }

  onExportClick() {
    const params$ = isObservable(this.gridExportParams)
      ? this.gridExportParams
      : of(this.gridExportParams);

    params$.subscribe(params => this.exportData(params));
  }

  private exportData({
    fileName,
    columnFilter,
    rowFilter,
    prepend,
    processCellCallback,
  }: GridExportParams<T>) {
    if (!this.source) {
      throw new Error('No source');
    }

    const source = this.source;
    assertRowDataProvider(source.rowDataProvider);

    // To be consistent with ag-grid
    fileName = fileName?.replace(/\./g, '_') || fileName;

    source.rowDataProvider.rowData$.pipe(firstWithContext()).subscribe(rowData => {
      if (rowData == null) {
        throw new Error('No rowData');
      }

      let columns = source.columns.toArray();
      if (columnFilter) {
        columns = columns.filter(c => columnFilter(c.field as string));
      }

      const rows = rowFilter ? rowData.filter(rowFilter) : rowData;

      this.csvBuilder.build(rows, columns, {
        fileName,
        prepend,
        processCellCallback,
      });
    });
  }
}

function assertRowDataProvider<T extends {}>(
  rowDataProvider: GridRowDataProvider<T>,
): asserts rowDataProvider is GridRowDataDirective<T> {
  if (!(rowDataProvider instanceof GridRowDataDirective)) {
    throw new Error('Unsupported rowDataProvider');
  }
}
